import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, X, ShieldCheck, Save, Trash2, Film } from "lucide-react";
import api from "../lib/api";
import { uid } from "../lib/uid";
import { Layout } from "../components/Layout";
import { OriginalityScan } from "../components/OriginalityScan";
import { useAuth } from "../context/AuthContext";

const AdminEditFact = () => {
  const { user, ready } = useAuth();
  const { slug } = useParams();
  const nav = useNavigate();

  const [subjects, setSubjects] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [form, setForm] = useState({ title: "", hook: "", full_fact: "", subject_slug: "", image_url: "" });
  const [tagsInput, setTagsInput] = useState("");
  const [sources, setSources] = useState([{ id: uid(), label: "", url: "" }]);
  const [related, setRelated] = useState([]);
  const [checks, setChecks] = useState({ ownWords: false, sources: false, image: false });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [videoUrl, setVideoUrl] = useState("");
  const [uploading, setUploading] = useState(0);

  useEffect(() => {
    if (ready && !user) nav("/admin", { replace: true });
  }, [ready, user, nav]);

  useEffect(() => {
    api.get("/subjects").then((r) => setSubjects(r.data));
    api.get(`/facts/${slug}`).then((r) => {
      const f = r.data;
      setForm({ title: f.title, hook: f.hook, full_fact: f.full_fact, subject_slug: f.subject_slug, image_url: f.image_url || "" });
      setTagsInput((f.tags || []).join(", "));
      setSources(f.sources?.length ? f.sources.map((s) => ({ ...s, id: uid() })) : [{ id: uid(), label: "", url: "" }]);
      setRelated(f.related || []);
      setVideoUrl(f.video_url || "");
      setChecks({
        ownWords: !!f.compliance?.ownWords,
        sources: !!f.compliance?.sources,
        image: !!f.compliance?.image,
      });
      setLoaded(true);
    }).catch(() => {
      toast.error("Curio not found");
      nav("/admin/curios", { replace: true });
    });
  }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps

  const tags = useMemo(
    () => tagsInput.split(",").map((t) => t.trim().toLowerCase()).filter(Boolean),
    [tagsInput]
  );
  const validSources = useMemo(() => sources.filter((s) => s.url.trim()), [sources]);

  const socialCaption = useMemo(() => {
    if (!form.hook && !form.title) return "";
    return `${form.hook} — "${form.title}" | Explore more curios at CurioVault.co`;
  }, [form.hook, form.title]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setSourceField = (i, field) => (e) =>
    setSources((rows) => rows.map((r, idx) => (idx === i ? { ...r, [field]: e.target.value } : r)));
  const addSourceRow = () => setSources((rows) => [...rows, { id: uid(), label: "", url: "" }]);
  const removeSourceRow = (i) => setSources((rows) => rows.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    if (validSources.length < 2) {
      toast.error("Please list at least 2 sources.");
      return;
    }
    setSaving(true);
    try {
      await api.put(`/facts/${slug}`, {
        ...form,
        tags,
        related,
        sources: validSources,
        compliance: { ownWords: checks.ownWords, sources: checks.sources, image: checks.image },
      });
      toast.success("Curio updated!");
      nav(`/curio/${slug}`);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not update the curio.");
    } finally {
      setSaving(false);
    }
  };

  const deleteCurio = async () => {
    if (!window.confirm(`Delete "${form.title || slug}" permanently?\n\nIt will be removed from the database and suppressed from re-seeding. This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/curios/${slug}`);
      toast.success("Curio deleted");
      nav("/admin/curios", { replace: true });
    } catch (err) {
      toast.error("Could not delete the curio.");
      setDeleting(false);
    }
  };

  const inputCls =
    "w-full font-ui text-sm px-3 py-2.5 bg-[#FAF7F0] border border-[#D8CEB9] rounded-sm outline-none focus:border-[#9E4535] focus:ring-2 focus:ring-[#F5E8EA]";

  if (!loaded) {
    return (
      <Layout>
        <div className="max-w-3xl mx-auto px-6 py-24 text-center font-body text-[#7A6C5D]">Opening the record…</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        <Link to="/admin/curios" className="font-ui text-sm text-[#9E4535] hover:underline">← Back to all curios</Link>
        <div className="flex items-start justify-between gap-4 mt-4">
          <div>
            <p className="caption-label mb-1">Curator Desk</p>
            <h1 className="font-serif-display text-3xl sm:text-4xl font-bold text-[#1A1615] mb-8">Edit Curio</h1>
          </div>
          <button
            type="button"
            onClick={deleteCurio}
            disabled={deleting}
            data-testid="delete-curio-button"
            className="shrink-0 inline-flex items-center gap-1.5 font-ui text-xs font-semibold px-4 py-2 border border-[#8A1E1E] text-[#8A1E1E] rounded-sm hover:bg-[#8A1E1E] hover:text-[#FAF7F0] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Trash2 className="w-3.5 h-3.5" /> {deleting ? "Deleting…" : "Delete Curio"}
          </button>
        </div>

        <form onSubmit={submit} className="space-y-6">
          <div className="bg-white border border-[#D8CEB9] rounded-sm p-6 space-y-5">
            <div>
              <label className="caption-label block mb-1.5">Title</label>
              <input data-testid="edit-title-input" value={form.title} onChange={set("title")} required className={inputCls} />
              <p className="font-mono-cv text-[11px] text-[#7A6C5D] mt-1.5">URL slug stays: /curio/{slug}</p>
            </div>
            <div>
              <label className="caption-label block mb-1.5">Short Hook</label>
              <input data-testid="edit-hook-input" value={form.hook} onChange={set("hook")} required className={inputCls} />
            </div>
            <div>
              <label className="caption-label block mb-1.5">Full Curio</label>
              <textarea data-testid="edit-body-input" value={form.full_fact} onChange={set("full_fact")} required rows={9} className={inputCls} />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="caption-label block mb-1.5">Subject</label>
                <select data-testid="edit-subject-select" value={form.subject_slug} onChange={set("subject_slug")} required className={inputCls}>
                  {subjects.map((s) => (
                    <option key={s.slug} value={s.slug}>{s.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="caption-label block mb-1.5">Tags <span className="normal-case tracking-normal text-[#B8AC9C]">(comma separated)</span></label>
                <input data-testid="edit-tags-input" value={tagsInput} onChange={(e) => setTagsInput(e.target.value)} className={inputCls} />
              </div>
            </div>
            <div>
              <label className="caption-label block mb-1.5">Hero Image URL</label>
              <input data-testid="edit-image-input" value={form.image_url} onChange={set("image_url")} className={inputCls} />
            </div>
            {form.image_url && (
              <div className="w-full aspect-[3/2] max-w-xs overflow-hidden rounded-sm border border-[#D8CEB9] bg-[#F3EDE0]">
                <img src={form.image_url} alt="preview" className="w-full h-full object-cover" />
              </div>
            )}
          </div>

          {/* Reel */}
          <div className="bg-white border border-[#D8CEB9] rounded-sm p-6" data-testid="edit-reel-section">
            <h2 className="font-serif-display text-lg font-semibold text-[#1A1615] flex items-center gap-2"><Film className="w-4 h-4 text-[#9E4535]" /> Reel video</h2>
            <p className="font-body text-sm text-[#7A6C5D] mt-1">Upload the MP4 of this curio's Reel. It replaces the hero image on the curio page (the image stays as the poster).</p>
            {videoUrl && (
              <video src={videoUrl} data-testid="edit-reel-preview" controls playsInline muted className="mt-4 w-full max-w-[240px] aspect-[9/16] object-cover rounded-sm border border-[#D8CEB9] bg-[#1A1615]" />
            )}
            <div className="flex flex-wrap items-center gap-3 mt-4">
              <label className="inline-flex items-center gap-2 cursor-pointer bg-[#1A1615] text-[#FAF7F0] hover:bg-black rounded-sm px-4 py-2 text-sm font-medium">
                <Film className="w-4 h-4" /> {uploading ? `Uploading ${uploading}%` : videoUrl ? "Replace reel" : "Upload reel (.mp4)"}
                <input type="file" accept="video/mp4,video/quicktime,video/webm" data-testid="edit-reel-input" className="hidden" disabled={!!uploading}
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    const fd = new FormData();
                    fd.append("file", file);
                    setUploading(1);
                    try {
                      const { data } = await api.post(`/admin/curios/${slug}/video`, fd, { onUploadProgress: (p) => setUploading(Math.max(1, Math.round((p.loaded / (p.total || 1)) * 100))) });
                      setVideoUrl(data.video_url);
                      toast.success("Reel uploaded and live on the curio page");
                    } catch (err) {
                      toast.error(err.response?.data?.detail || "Reel upload failed");
                    } finally { setUploading(0); e.target.value = ""; }
                  }} />
              </label>
              {videoUrl && (
                <button type="button" data-testid="edit-reel-remove" onClick={async () => { await api.delete(`/admin/curios/${slug}/video`); setVideoUrl(""); toast.success("Reel removed"); }}
                  className="text-sm text-[#9E4535] hover:underline">Remove reel</button>
              )}
            </div>
          </div>

          {/* Sources */}
          <div className="bg-white border border-[#D8CEB9] rounded-sm p-6" data-testid="edit-sources-editor">
            <div className="flex items-center justify-between mb-1">
              <h2 className="font-serif-display text-lg font-semibold text-[#1A1615]">Sources &amp; Further Reading</h2>
              <span className="font-mono-cv text-[11px] text-[#7A6C5D]">{validSources.length} listed · min 2</span>
            </div>
            <div className="space-y-3 mt-3">
              {sources.map((s, i) => (
                <div key={s.id} className="flex gap-2 items-start" data-testid={`edit-source-row-${i}`}>
                  <input value={s.label} onChange={setSourceField(i, "label")} placeholder="Label" data-testid={`edit-source-label-${i}`} className={`${inputCls} sm:max-w-[38%]`} />
                  <input value={s.url} onChange={setSourceField(i, "url")} placeholder="https://…" data-testid={`edit-source-url-${i}`} className={inputCls} />
                  {sources.length > 1 && (
                    <button type="button" onClick={() => removeSourceRow(i)} className="mt-1 text-[#7A6C5D] hover:text-[#9E4535]" aria-label="Remove source">
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <button type="button" onClick={addSourceRow} data-testid="edit-add-source" className="mt-3 inline-flex items-center gap-1.5 font-ui text-xs font-semibold text-[#9E4535] hover:underline">
              <Plus className="w-3.5 h-3.5" /> Add another source
            </button>
          </div>

          <OriginalityScan fullFact={form.full_fact} sources={validSources} />

          {/* Auto social caption (hidden field, regenerated on save) */}
          {socialCaption && (
            <div className="bg-[#FDF8ED] border border-[#E4D3A8] rounded-sm p-5" data-testid="edit-social-caption-preview">
              <p className="caption-label !text-[#B08A42] mb-2">Auto-generated Social Caption</p>
              <p className="font-body text-sm text-[#4A4036] italic">{socialCaption}</p>
              <p className="font-mono-cv text-[10px] text-[#B8AC9C] mt-2">Hidden field · regenerates from Hook + Title on save · used for future auto-posting.</p>
            </div>
          )}

          {/* Compliance status */}
          <div className="bg-[#F5E8EA] border border-[#E4C9CD] rounded-sm p-6" data-testid="edit-compliance-checklist">
            <div className="flex items-center gap-2 mb-4">
              <ShieldCheck className="w-4 h-4 text-[#9E4535]" />
              <h2 className="font-serif-display text-lg font-semibold text-[#1A1615]">Compliance Status</h2>
            </div>
            <div className="space-y-3 font-body text-sm text-[#4A4036]">
              <label className="flex gap-3 items-start cursor-pointer">
                <input type="checkbox" checked={checks.ownWords} onChange={(e) => setChecks((c) => ({ ...c, ownWords: e.target.checked }))} data-testid="edit-check-own-words" className="accent-[#9E4535] w-4 h-4 mt-0.5" />
                <span>Written in my <strong>OWN words</strong>; no more than 10 words in a row copied from any source.</span>
              </label>
              <label className="flex gap-3 items-start cursor-pointer">
                <input type="checkbox" checked={checks.sources} onChange={(e) => setChecks((c) => ({ ...c, sources: e.target.checked }))} data-testid="edit-check-sources" className="accent-[#9E4535] w-4 h-4 mt-0.5" />
                <span>At least <strong>2 sources</strong> listed.</span>
              </label>
              <label className="flex gap-3 items-start cursor-pointer">
                <input type="checkbox" checked={checks.image} onChange={(e) => setChecks((c) => ({ ...c, image: e.target.checked }))} data-testid="edit-check-image" className="accent-[#9E4535] w-4 h-4 mt-0.5" />
                <span>Image is <strong>copyright-free</strong> (owned, or Unsplash/Pexels/Wikimedia Commons with license checked).</span>
              </label>
            </div>
          </div>

          <button
            type="submit"
            disabled={saving || validSources.length < 2}
            data-testid="update-fact-button"
            className="inline-flex items-center gap-2 font-ui text-sm font-semibold px-6 py-3 bg-[#9E4535] text-[#FAF7F0] rounded-sm hover:bg-[#82382B] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save Changes"}
          </button>
        </form>
      </div>
    </Layout>
  );
};

export default AdminEditFact;
